import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { Uuid, uuidSerializer } from "@megaron/uuid";

export const reportCellWidths = ["full", "half", "third", "quarter"] as const;

export type ReportCellWidth = (typeof reportCellWidths)[number];

export const reportCellSerializer = Serializers.object({
  uuid: uuidSerializer,
  title: Serializers.string,
  width: Serializers.stringOneOf(...reportCellWidths),
  visible: Serializers.boolean,
  code: Serializers.string,
});

export type ReportCell = SerializerValue<typeof reportCellSerializer>;

export const reportSerializer = Serializers.object({
  uuid: uuidSerializer,
  title: Serializers.string,
  description: Serializers.string,
  cells: reportCellSerializer.array(),
});

export type Report = SerializerValue<typeof reportSerializer>;

export type ReportMedia = { type: "text/html" | "image/png"; data: string };

export type ReportCellResult = {
  uuid: Uuid;
  status: "ok" | "error";
  error: string | null;
  stdout: string;
  stderr: string;
  columns: string[];
  rows: unknown[][];
  media: ReportMedia[];
};

export const reports = {
  reportsQuery: HttpAction({
    path: "/reports",
    method: "get",
    requiresAuth: true as const,
    valueSerializer: reportSerializer.array(),
  }),

  reportQuery: HttpAction({
    path: "/report/:uuid",
    method: "get",
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    requiresAuth: true as const,
    valueSerializer: reportSerializer,
    errorSerializer: Serializers.stringOneOf("ReportNotFound"),
  }),

  reportOutputsQuery: HttpAction({
    path: "/report/:uuid/outputs",
    method: "get",
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    qsSerializer: Serializers.object<{ start: string; end: string; refresh?: boolean }>({
      start: Serializers.string,
      end: Serializers.string,
      refresh: Serializers.booleanString.optional(),
    }),
    requiresAuth: true as const,
    valueSerializer: Serializers.identity<ReportCellResult[]>(),
    errorSerializer: Serializers.stringOneOf("ReportNotFound", "ReportRunFailed"),
  }),

  saveReport: HttpAction({
    path: "/report",
    method: "put",
    requiresAuth: true as const,
    bodySerializer: reportSerializer,
    valueSerializer: reportSerializer,
  }),

  removeReport: HttpAction({
    path: "/report/:uuid",
    method: "delete",
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    requiresAuth: true as const,
  }),
};
